import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { AvForm, AvGroup, AvField } from "availity-reactstrap-validation";
import { Col, Label } from "reactstrap";
import { enumsListStatus } from "utils/enums";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import Select from "react-select";
import useList from "hooks/useList";
import usePaginatedSelect from "customsHooks/usePaginatedSelect";
import { isNullOrUndefined } from "utils/validations";

const ComboBox = (props) => {
  const { t } = useTranslation();
  const {
    id,
    label,
    hide,
    required,
    isPreview,
    isFilterForm,
    itemLink,
    handleOnChangeRecord,
    value,
  } = props;

  const { getItems } = useList();
  const [selectedOption, setSelectedOption] = useState(null);
  const [hiddenFieldValue, setHiddenFieldValue] = useState("");

  let filterFn = null;

  if (isFilterForm === false) {
    filterFn = (item) => item.status === enumsListStatus.ACTIVE;
  }

  const {
    options,
    isLoading,
    loadInitial,
    loadInitialDebounced,
    loadMore,
    reset,
    resetSearch,
  } = usePaginatedSelect({
    fetchFn: (page, perPage, search) => getItems(itemLink, page, perPage, search),
    formatFn: (item) => {
      let itemValue = null;

      if (isFilterForm === true) {
        itemValue = item.name;
      } else {
        itemValue = item.uuid;
      }

      return {
        value: itemValue,
        label: item.name,
      };
    },
    filterFn: filterFn,
  });

  let hideLabelComboBox = null;
  let selectPlaceholder = null;

  /**
   * Sets the placeholder text based on the loading state.
   * @param {boolean} isLoading - A flag indicating whether items are currently being loaded.
   * @returns {string} The localized text to be displayed in the combo box placeholder.
   */
  if (isLoading === true) {
    selectPlaceholder = t("loading");
  } else {
    selectPlaceholder = t("form.designerLabel10.1");
  }

  /**
   * Conditionally generates a JSX element to hide or show a labeled comboBox.
   * @param {boolean} hide - A boolean flag indicating whether to hide the labeled comboBox.
   * @param {string} t - A function for translating text labels.
   * @returns {JSX.Element|null} - A JSX element representing a labeled comboBox with optional hiding text, or null if 'hide' is false.
   */
  if (hide === true) {
    hideLabelComboBox = <i className="hide-textBox custom-opacity">({t("form.field49")})</i>;
  } else {
    hideLabelComboBox = null;
  }

  /**
   * Synchronizes the local selected option state with the value prop from the parent.
   * When the value prop changes, updates the local state to reflect the correct selection.
   * @param {object} value - The object containing uuid and name properties from the parent.
   * @param {boolean} isFilterForm - Whether the component is used in a filter form context.
   * @returns {void}
   */
  useEffect(() => {
    if (
      value !== undefined &&
      value !== null &&
      typeof value === "object" &&
      value.name !== null &&
      value.name !== undefined
    ) {
      let matchValue = null;
      if (isFilterForm === true) {
        matchValue = value.name;
      } else {
        matchValue = value.uuid;
      }

      setSelectedOption({ value: matchValue, label: value.name });
      setHiddenFieldValue(matchValue);
    }
  }, [value, isFilterForm]);

  /**
   * Handles the change event for the combo box, updates the local selected state
   * and notifies the parent with the new value.
   * @param {object|null} option - The selected option from react-select.
   */
  const handleChangeComboBox = (option) => {
    setSelectedOption(option);

    let newValue = "";
    if (isNullOrUndefined(option) === false) {
      newValue = option.value;
    }

    setHiddenFieldValue(newValue);
    handleOnChangeRecord(id, newValue);
  };

  /**
   * Handles the menu close event for the combo box.
   * Resets the search term and reloads the full list of options.
   * @returns {void}
   */
  const handleMenuClose = () => {
    resetSearch();
    loadInitial();
  };

  /**
   * Handles the input change event for the combo box, triggering a debounced search for options based on user input.
   * When the user types in the combo box, this function is called with the new input value and the action type.
   * If the action type is "input-change", it calls the debounced loadInitial function to fetch options matching the input.
   * @param {string} inputValue - The current value of the input field in the combo box.
   * @param {object} action - An object containing details about the input change event, including the action type.
   * @returns {void}
   */
  const handleInputChange = (inputValue, action) => {
    if (action.action === "input-change") {
      loadInitialDebounced(inputValue);
    }
  };

  /**
   * Loads the initial options when the component mounts or when itemLink/hide changes.
   * Resets the paginated state and fetches the first page of items.
   * Skips fetching if the component is not in preview mode and is hidden.
   * @param {string} itemLink - The link or identifier for the items to be fetched.
   * @param {boolean} hide - Whether the field is hidden.
   * @returns {void}
   */
  useEffect(() => {
    if (isPreview === false && hide === true) return;
    reset();
    loadInitial();
    // eslint-disable-next-line
  }, [itemLink, hide]);

  const selectStyles = {
    option: (provided) => ({
      ...provided,
      wordBreak: "break-word",
      overflowWrap: "break-word",
      whiteSpace: "normal",
    }),
    singleValue: (provided) => ({
      ...provided,
      wordBreak: "break-word",
      overflowWrap: "break-word",
    }),
  };

  const selectComponent = (
    <>
      <Select
        id={id}
        name={id}
        placeholder={selectPlaceholder}
        isLoading={isLoading}
        options={options}
        value={selectedOption}
        onChange={handleChangeComboBox}
        onMenuClose={handleMenuClose}
        onMenuScrollToBottom={loadMore}
        onInputChange={handleInputChange}
        filterOption={null}
        isClearable
        styles={selectStyles}
      />
      <AvField
        type="hidden"
        name={`${id}_validation`}
        value={hiddenFieldValue}
        errorMessage={t("form.field46")}
        required={required}
      />
    </>
  );

  /**
   * Renders a form with a react-select field conditionally based on the preview, hide, and filter form status.
   * @param {boolean} isPreview - Determines if the component is in preview mode.
   * @param {boolean} hide - Determines if the component should be hidden.
   * @param {boolean} isFilterForm - Determines if the component is part of a filter form.
   * @param {string} id - The unique identifier for the select field.
   * @param {string} label - The label text for the select field.
   * @param {boolean} required - Determines if the select field is required.
   * @param {Function} handleChangeComboBox - The function to handle change events for the select field.
   * @param {JSX.Element} hideLabelComboBox - The element to display when in preview mode.
   * @param {Array} options - The list of formatted options for react-select.
   * @param {object|null} selectedValue - The currently selected option.
   * @param {string} selectPlaceholder - The placeholder text for the combo box.
   * @param {boolean} isLoading - Whether items are currently loading.
   * @returns {JSX.Element|null} The rendered form with the select field or null if hidden.
   */
  if (isPreview === true) {
    return (
      <AvForm>
        <AvGroup row>
          <Label
            className={cx("", {
              "is-required": required === true,
            })}
            sm={3}
          >
            {label} {hideLabelComboBox}
          </Label>
          <Col md={8}>
            {selectComponent}
          </Col>
        </AvGroup>
      </AvForm>
    );
  } else if (hide === false && isFilterForm === false) {
    return (
      <AvGroup row>
        <Label
          className={cx("", {
            "is-required": required === true,
          })}
          sm={3}
        >
          {label}
        </Label>
        <Col md={8}>
          {selectComponent}
        </Col>
      </AvGroup>
    );
  } else if (isFilterForm === true) {
    return (
      <Col md={12}>
        {selectComponent}
      </Col>
    );
  } else {
    return null;
  }
};

ComboBox.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  hide: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  isPreview: PropTypes.bool.isRequired,
  isFilterForm: PropTypes.bool,
  itemLink: PropTypes.string.isRequired,
  handleOnChangeRecord: PropTypes.func.isRequired,
  value: PropTypes.object,
};

export default React.memo(ComboBox);
